var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Trans, useTranslation } from "react-i18next";
import { message } from "antd";
import { omit } from "lodash";
import { Permissions } from "@whatsper/texterchat-common";
import { useStore } from "../../../../store/rootStore";
import { Operation } from "../../../EntityManager";
import { ManagerDetailsView } from "../core";
import { getConfig } from "../../../../settings";
import ChannelsFormWrapper from "./components/ChannelsForm/ChannelsFormWrapper";
const ChannelsDetailsView = observer(({ entityId, operation, onCancel, onCompleted, }) => {
    var _a;
    const { channels, agentsStore } = useStore();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [inboxConfig, setInboxConfig] = useState(null);
    const formRef = useRef(null);
    useEffect(() => {
        getConfig()
            .then((config) => setInboxConfig(config))
            .catch((error) => console.error("Error getting inbox config", error));
    }, []);
    const entity = (() => {
        if (!entityId || operation === Operation.CREATE)
            return null;
        return channels.accounts.find((c) => c._id === entityId) || null;
    })();
    const renderDeleteQuestion = (channel) => {
        return React.createElement(Trans, { i18nKey: "sureDeleteName", values: { name: channel.title } });
    };
    const errorHandler = (formData) => {
        if (!formData.adapterName) {
            throw new Error(t("adapterRequired"));
        }
        if (formRef.current && !formRef.current.validateForm()) {
            throw new Error(t("credentialsNotValid"));
        }
    };
    const handleSave = (formData, existingEntity) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        setLoading(true);
        try {
            errorHandler(formData);
            if (existingEntity) {
                yield channels.editChannelAccount(Object.assign(Object.assign({}, existingEntity), omit(formData, "credentials")), formData.credentials);
                message.success(React.createElement(Trans, { i18nKey: "channelEditSuccess", values: { name: formData.title ||
                            existingEntity.title ||
                            ((_a = existingEntity.metadata) === null || _a === void 0 ? void 0 : _a.accountTitle) ||
                            formData.accountId ||
                            existingEntity.accountId
                    } }));
            }
            else {
                const { adapterName } = formData, account = __rest(formData, ["adapterName"]);
                if (adapterName) {
                    yield channels.createChannelAccount(adapterName, 
                    // @ts-expect-error - credentials validated in errorHandler
                    account);
                    message.success(React.createElement(Trans, { i18nKey: "channelCreateSuccess", values: { name: formData.title || formData.accountId } }));
                }
            }
            // refresh channel accounts as we don't have emit events
            yield channels.fetchChannelsAccounts();
        }
        catch (error) {
            console.error("Failed to save channel:", error);
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            message.error(errorMessage);
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    const handleDelete = (channel) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            yield channels.deleteChannelAccount(channel);
            message.success(React.createElement(Trans, { i18nKey: "channelDeleteSuccess", values: { name: channel.title } }));
            yield new Promise((resolve) => setTimeout(resolve, 2000));
            window.location.reload();
        }
        catch (error) {
            console.error("Failed to delete channel:", error);
            message.error(React.createElement(Trans, { i18nKey: "channelDeleteError" }));
            throw error;
        }
        finally {
            setLoading(false);
        }
    });
    const canDelete = agentsStore.hasPermissions([Permissions.Permission.channels_manage]) &&
        ((_a = inboxConfig === null || inboxConfig === void 0 ? void 0 : inboxConfig.features) === null || _a === void 0 ? void 0 : _a.channelsDelete);
    return (React.createElement(ManagerDetailsView, { entity: entity, entityId: entityId, operation: operation, form: ChannelsFormWrapper, formProps: {
            getChannelAccount: channels.getChannelAccount,
            formRef,
        }, loading: loading, initialLoading: !channels.accounts.length, renderDeleteQuestion: renderDeleteQuestion, onEntitySave: handleSave, onEntityDelete: canDelete ? handleDelete : undefined, onCancel: onCancel, onSaveCompleted: onCompleted, onDeleteCompleted: onCompleted }));
});
export default ChannelsDetailsView;
